import React, {
    CSSProperties,
    PropsWithChildren,
    useEffect,
    useRef,
    useState,
} from "react";
import cn from "classnames";
import PageWrapper from "../../../../../../components/PageWrapper/PageWrapper";
import { ButtonProps } from "@evidenceb/athena-common/design-system/Button";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import PageHeader, {
    Props as PageHeaderProps,
} from "../../../../../../components/PageHeader/PageHeader";
import OfflineBanner from "../../../../../OfflineBanner/OfflineBanner";
import ButtonWithLoadingState, {
    Props as ButtonWithLoadingStateProps,
} from "../../../../../../components/ButtonWithLoadingState/ButtonWithLoadingState";

import "./EditorLayout.scss";

export const EditorContainer = ({
    children,
    className,
}: PropsWithChildren<{
    className?: string;
}>) => {
    return (
        <PageWrapper className={cn("editor-container", className)}>
            <OfflineBanner />

            {children}
        </PageWrapper>
    );
};

export const EditorBody = ({ children }: PropsWithChildren) => {
    return <div className="editor-body">{children}</div>;
};

export const EditorPanel = ({
    children,
    ...props
}: PropsWithChildren<PageHeaderProps>) => {
    return (
        <div className="editor-panel">
            <PageHeader {...props} /> {children}
        </div>
    );
};

export const EditorFooter = ({
    button1,
    button2,
}: {
    button1: Pick<ButtonProps, "label" | "onPress" | "disabled">;
    button2: Pick<
        ButtonWithLoadingStateProps,
        "label" | "onPress" | "disabled" | "loading"
    >;
}) => {
    // Make sure the buttons are the same size
    const firstBtnRef = useRef<HTMLButtonElement>(null);
    const secondBtnRef = useRef<HTMLButtonElement>(null);
    const [btnWidth, setBtnWidth] = useState<number>();
    useEffect(() => {
        if (!firstBtnRef.current || !secondBtnRef.current) return;
        const biggerBtnWidth =
            firstBtnRef.current.clientWidth > secondBtnRef.current.clientWidth
                ? firstBtnRef.current.clientWidth
                : secondBtnRef.current.clientWidth;
        setBtnWidth(biggerBtnWidth);
    }, []);

    return (
        <div className="editor-footer">
            <Button
                ref={firstBtnRef}
                {...button1}
                style={{ width: btnWidth }}
            />
            <ButtonWithLoadingState
                ref={secondBtnRef}
                {...button2}
                style={{ width: btnWidth }}
                variant="custom"
                colors={{
                    background: "var(--element-teacher-default)",
                    text: "var(--text-main-inverted)",
                    hoverBackground: "var(--element-button-custom-hover)",
                }}
            />
        </div>
    );
};

export const EditorMainContent = ({
    children,
    className,
    style,
}: PropsWithChildren<{ className?: string; style?: CSSProperties }>) => {
    const { isTabletOrSmaller } = useBreakpoints();
    if (isTabletOrSmaller) return null;
    return (
        <div className={cn("editor-main-content", className)} style={style}>
            {children}
        </div>
    );
};
